import { defineStore } from 'pinia'
import { type Ref, computed, onScopeDispose, ref, watch } from 'vue'

import { i18n } from '@/i18n'
import type { Subscription } from '@/lib/firebase/subscribe'
import { createRetainer, onRetainedTeardown } from '@/lib/live/retainer'
import { parseCallableError } from '@/lib/firebase/errors'
import { saveTextFile } from '@/lib/save-text-file'
import {
  attachDesignToPost as repoAttachDesignToPost,
  attachPublishedUrl as repoAttachPublishedUrl,
  exportArticle as repoExportArticle,
  recheckArticleHealth as repoRecheckArticleHealth,
  readArticleLinks as repoReadArticleLinks,
  cancelSchedule as repoCancelSchedule,
  createPost as repoCreatePost,
  deletePost as repoDeletePost,
  editArticleMeta as repoEditArticleMeta,
  editPostBody as repoEditPostBody,
  getNextOpenSlot as repoGetNextOpenSlot,
  promoteArticle as repoPromoteArticle,
  publishPostNow as repoPublishPostNow,
  reschedulePost as repoReschedulePost,
  restoreRevision as repoRestoreRevision,
  schedulePost as repoSchedulePost,
  schedulePostsInPeriod as repoSchedulePostsInPeriod,
  setPostPublishAt as repoSetPostPublishAt,
  subscribeToArticles as repoSubscribeToArticles,
  subscribeToPostResults as repoSubscribeToPostResults,
  subscribeToPostRevisions as repoSubscribeToPostRevisions,
  subscribeToPosts as repoSubscribeToPosts,
  type AttachDesignToPostInput,
  type AttachPublishedUrlInput,
  type ExportArticleInput,
  type RecheckArticleHealthInput,
  type RecheckArticleHealthResult,
  type CancelScheduleInput,
  type CreatePostInput,
  type DeletePostInput,
  type EditArticleMetaInput,
  type EditPostBodyInput,
  type GetNextOpenSlotInput,
  type PromoteArticleInput,
  type PromoteArticleResult,
  type PublishPostNowInput,
  type ReschedulePostInput,
  type RestoreRevisionInput,
  type SchedulePostInput,
  type SchedulePostsInPeriodInput,
  type SetPostPublishAtInput,
} from '@/repositories/content'
import { useAuthStore } from '@/stores/auth'
import { useToastStore } from '@/stores/toast'
import type {
  ArticleDoc,
  ArticleLinks,
  ContentItem,
  ContentRevision,
  NextOpenSlot,
  PostResults,
} from '@/types/content'
import { failed, isLoaded, loaded, loading, type LoadState } from '@/types/load-state'

interface ContentScope {
  workspaceId: string
  brandId: string
}

/** The server's `PreconditionError` reason when a design page has no render at schedule/publish time. */
const RENDER_MISSING_REASON = 'design_page_render_missing'

const ARTICLE_NOT_VERIFIED_REASON = 'article_not_verified'

export const useContentStore = defineStore('content', () => {
  const posts = ref<LoadState<ContentItem[]>>(loading()) as Ref<LoadState<ContentItem[]>>
  const revisions = ref<LoadState<ContentRevision[]>>(loading()) as Ref<
    LoadState<ContentRevision[]>
  >
  const revisionsPostId = ref<string | null>(null)
  const postResults = ref<LoadState<PostResults[]>>(loading()) as Ref<LoadState<PostResults[]>>
  const articles = ref<LoadState<ArticleDoc[]>>(loading()) as Ref<LoadState<ArticleDoc[]>>
  const articleLinksByPostId = ref<Record<string, LoadState<ArticleLinks>>>({})
  const linkReads = new Map<string, number>()
  let lastLinkRead = 0

  let postsSub: Subscription<ContentItem[]> | null = null
  let stopPostsWatch: (() => void) | null = null

  let revisionsSub: Subscription<ContentRevision[]> | null = null
  let stopRevisionsWatch: (() => void) | null = null

  let postResultsSub: Subscription<PostResults[]> | null = null
  let stopPostResultsWatch: (() => void) | null = null

  let articlesSub: Subscription<ArticleDoc[]> | null = null
  let stopArticlesWatch: (() => void) | null = null

  function currentScope(): ContentScope | null {
    const auth = useAuthStore()
    const workspaceId = auth.claims?.workspaceId
    const brandId = auth.claims?.brandId
    if (!workspaceId || !brandId) return null
    return { workspaceId, brandId }
  }

  function subscribeToPostResults(): void {
    if (postResultsSub) return

    const scope = currentScope()
    if (!scope) {
      postResults.value = failed(i18n.global.t('common.noWorkspaceSelected'))
      return
    }

    postResults.value = loading()
    const sub = repoSubscribeToPostResults(scope)
    postResultsSub = sub
    stopPostResultsWatch = watch(
      sub.state,
      (state) => {
        postResults.value = state
      },
      { immediate: true },
    )
  }

  function unsubscribeFromPostResults(): void {
    postResultsSub?.stop()
    postResultsSub = null
    stopPostResultsWatch?.()
    stopPostResultsWatch = null
    postResults.value = loading()
  }

  function subscribeToArticles(): void {
    if (articlesSub) return

    const scope = currentScope()
    if (!scope) {
      articles.value = failed(i18n.global.t('common.noWorkspaceSelected'))
      return
    }

    articles.value = loading()
    const sub = repoSubscribeToArticles(scope)
    articlesSub = sub
    stopArticlesWatch = watch(
      sub.state,
      (state) => {
        articles.value = state
      },
      { immediate: true },
    )
  }

  function unsubscribeFromArticles(): void {
    articlesSub?.stop()
    articlesSub = null
    stopArticlesWatch?.()
    stopArticlesWatch = null
    articles.value = loading()
  }

  function openFeeds(): void {
    subscribeToPostResults()
    subscribeToArticles()

    if (postsSub) return

    const scope = currentScope()
    if (!scope) {
      posts.value = failed(i18n.global.t('common.noWorkspaceSelected'))
      return
    }

    posts.value = loading()
    const sub = repoSubscribeToPosts(scope)
    postsSub = sub
    stopPostsWatch = watch(
      sub.state,
      (state) => {
        posts.value = state
      },
      { immediate: true },
    )
  }

  function closeFeeds(): void {
    postsSub?.stop()
    postsSub = null
    stopPostsWatch?.()
    stopPostsWatch = null
    posts.value = loading()
    unsubscribeFromPostResults()
    unsubscribeFromArticles()
  }

  const feedsRetainer = createRetainer({ open: openFeeds, close: closeFeeds })
  onScopeDispose(feedsRetainer.dispose)
  onScopeDispose(
    onRetainedTeardown(() => {
      unsubscribeFromRevisions()
      articleLinksByPostId.value = {}
      linkReads.clear()
    }),
  )

  function stopRevisionsSubscription(): void {
    revisionsSub?.stop()
    revisionsSub = null
    stopRevisionsWatch?.()
    stopRevisionsWatch = null
  }

  function subscribeToRevisions(postId: string): void {
    if (revisionsSub && revisionsPostId.value === postId) return

    const scope = currentScope()
    stopRevisionsSubscription()
    revisionsPostId.value = postId

    if (!scope) {
      revisions.value = failed(i18n.global.t('common.noWorkspaceSelected'))
      return
    }

    revisions.value = loading()
    const sub = repoSubscribeToPostRevisions({ ...scope, postId })
    revisionsSub = sub
    stopRevisionsWatch = watch(
      sub.state,
      (state) => {
        revisions.value = state
      },
      { immediate: true },
    )
  }

  function unsubscribeFromRevisions(): void {
    stopRevisionsSubscription()
    revisionsPostId.value = null
    revisions.value = loading()
  }

  async function runWrite<TInput>(
    action: (input: TInput) => Promise<unknown>,
    input: TInput,
    errorTitle: string,
    reasonMessages: Record<string, string> = {},
  ): Promise<boolean> {
    try {
      await action(input)
      return true
    } catch (cause) {
      const parsed = parseCallableError(cause)
      const message =
        parsed.reason === RENDER_MISSING_REASON
          ? i18n.global.t('content.renderGate.missingRenderMessage')
          : ((parsed.reason ? reasonMessages[parsed.reason] : undefined) ?? parsed.message)
      useToastStore().error(errorTitle, message)
      return false
    }
  }

  async function editPostBody(input: EditPostBodyInput): Promise<boolean> {
    return runWrite(repoEditPostBody, input, i18n.global.t('content.toast.saveEditFailed'))
  }

  async function getNextOpenSlot(input: GetNextOpenSlotInput): Promise<NextOpenSlot | false> {
    try {
      return await repoGetNextOpenSlot(input)
    } catch (cause) {
      const parsed = parseCallableError(cause)
      useToastStore().error(i18n.global.t('content.toast.nextSlotFailed'), parsed.message)
      return false
    }
  }

  async function restoreRevision(input: RestoreRevisionInput): Promise<boolean> {
    return runWrite(repoRestoreRevision, input, i18n.global.t('content.toast.restoreFailed'))
  }

  async function schedulePost(input: SchedulePostInput): Promise<boolean> {
    return runWrite(repoSchedulePost, input, i18n.global.t('content.toast.scheduleFailed'))
  }

  async function publishPostNow(input: PublishPostNowInput): Promise<boolean> {
    return runWrite(repoPublishPostNow, input, i18n.global.t('content.toast.publishFailed'))
  }

  async function reschedulePost(input: ReschedulePostInput): Promise<boolean> {
    return runWrite(repoReschedulePost, input, i18n.global.t('content.toast.rescheduleFailed'))
  }

  async function setPostPublishAt(input: SetPostPublishAtInput): Promise<boolean> {
    return runWrite(repoSetPostPublishAt, input, i18n.global.t('content.toast.setPublishAtFailed'))
  }

  async function cancelSchedule(input: CancelScheduleInput): Promise<boolean> {
    return runWrite(repoCancelSchedule, input, i18n.global.t('content.toast.cancelScheduleFailed'))
  }

  async function deletePost(input: DeletePostInput): Promise<boolean> {
    return runWrite(repoDeletePost, input, i18n.global.t('content.toast.deleteFailed'))
  }

  async function schedulePostsInPeriod(input: SchedulePostsInPeriodInput): Promise<boolean> {
    return runWrite(
      repoSchedulePostsInPeriod,
      input,
      i18n.global.t('content.toast.schedulePeriodFailed'),
    )
  }

  async function createPost(input: CreatePostInput): Promise<string | false> {
    try {
      const result = await repoCreatePost(input)
      return result.postId
    } catch (cause) {
      const parsed = parseCallableError(cause)
      useToastStore().error(i18n.global.t('content.toast.createFailed'), parsed.message)
      return false
    }
  }

  async function attachDesignToPost(input: AttachDesignToPostInput): Promise<boolean> {
    return runWrite(
      repoAttachDesignToPost,
      input,
      i18n.global.t('content.toast.attachDesignFailed'),
    )
  }

  /** Starts the post that promotes a verified article; resolves the run id, or false on failure (toasted). */
  async function promoteArticle(input: PromoteArticleInput): Promise<PromoteArticleResult | false> {
    try {
      return await repoPromoteArticle(input)
    } catch (cause) {
      const parsed = parseCallableError(cause)
      const message =
        parsed.reason === ARTICLE_NOT_VERIFIED_REASON
          ? i18n.global.t('content.toast.promoteArticleNotVerified')
          : parsed.message
      useToastStore().error(i18n.global.t('content.toast.promoteArticleFailed'), message)
      return false
    }
  }

  async function editArticleMeta(input: EditArticleMetaInput): Promise<boolean> {
    return runWrite(
      repoEditArticleMeta,
      input,
      i18n.global.t('content.toast.editArticleMetaFailed'),
    )
  }

  async function attachPublishedUrl(input: AttachPublishedUrlInput): Promise<boolean> {
    return runWrite(
      repoAttachPublishedUrl,
      input,
      i18n.global.t('content.toast.attachPublishedUrlFailed'),
    )
  }

  /** Renders the article for the founder's CMS and hands the file to the browser; `false` on failure (toasted). */
  async function exportArticle(input: ExportArticleInput): Promise<boolean> {
    try {
      const exported = await repoExportArticle(input)
      saveTextFile(
        exported.filename,
        exported.content,
        exported.format === 'html' ? 'text/html' : 'text/markdown',
      )
      return true
    } catch (cause) {
      useToastStore().error(
        i18n.global.t('content.toast.exportArticleFailed'),
        parseCallableError(cause).message,
      )
      return false
    }
  }

  /** Reads one article's text and topic links resolved against the topic now; not a live subscription. */
  async function readArticleLinks(postId: string): Promise<void> {
    const read = ++lastLinkRead
    linkReads.set(postId, read)
    const settle = (state: LoadState<ArticleLinks>) => {
      if (linkReads.get(postId) === read) {
        articleLinksByPostId.value = { ...articleLinksByPostId.value, [postId]: state }
      }
    }
    settle(loading())
    try {
      settle(loaded(await repoReadArticleLinks({ postId })))
    } catch (cause) {
      settle(failed(parseCallableError(cause).message))
    }
  }

  /** The last read of one article's resolved links, or `undefined` before the first read. */
  function articleLinksFor(postId: string): LoadState<ArticleLinks> | undefined {
    return articleLinksByPostId.value[postId]
  }

  /** The article post whose live page Raldo is reading again; `null` while none is. */
  const recheckingPostId = ref<string | null>(null)

  /** Reads the live page again; resolves whether it now carries every link, or `false` on failure (toasted). */
  async function recheckArticleHealth(
    input: RecheckArticleHealthInput,
  ): Promise<RecheckArticleHealthResult | false> {
    recheckingPostId.value = input.postId
    try {
      return await repoRecheckArticleHealth(input)
    } catch (cause) {
      const parsed = parseCallableError(cause)
      useToastStore().error(
        i18n.global.t('content.toast.recheckArticleHealthFailed'),
        parsed.message,
      )
      return false
    } finally {
      recheckingPostId.value = null
    }
  }

  const drafts = computed<ContentItem[]>(() =>
    isLoaded(posts.value) ? posts.value.value.filter((post) => post.status === 'draft') : [],
  )

  const scheduled = computed<ContentItem[]>(() => {
    if (!isLoaded(posts.value)) return []
    return posts.value.value
      .filter((post) => post.status === 'scheduled')
      .sort((a, b) => (a.scheduledAt?.getTime() ?? 0) - (b.scheduledAt?.getTime() ?? 0))
  })

  const published = computed<ContentItem[]>(() => {
    if (!isLoaded(posts.value)) return []
    return posts.value.value
      .filter((post) => post.status === 'published')
      .sort((a, b) => (b.publishedAt?.getTime() ?? 0) - (a.publishedAt?.getTime() ?? 0))
  })

  const failedPosts = computed<ContentItem[]>(() =>
    isLoaded(posts.value) ? posts.value.value.filter((post) => post.status === 'failed') : [],
  )

  const draftCount = computed<number>(() => drafts.value.length)

  function resultsForPost(postId: string): PostResults | undefined {
    if (!isLoaded(postResults.value)) return undefined
    return postResults.value.value.find((result) => result.postId === postId)
  }

  return {
    posts,
    revisions,
    revisionsPostId,
    postResults,
    retain: feedsRetainer.retain,
    subscribeToRevisions,
    unsubscribeFromRevisions,
    articles,
    promoteArticle,
    editArticleMeta,
    attachPublishedUrl,
    exportArticle,
    readArticleLinks,
    articleLinksFor,
    recheckArticleHealth,
    recheckingPostId,
    resultsForPost,
    editPostBody,
    getNextOpenSlot,
    restoreRevision,
    schedulePost,
    publishPostNow,
    reschedulePost,
    setPostPublishAt,
    cancelSchedule,
    deletePost,
    schedulePostsInPeriod,
    createPost,
    attachDesignToPost,
    drafts,
    scheduled,
    published,
    failed: failedPosts,
    draftCount,
  }
})
